import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { LogOut, Moon, Sun, User as UserIcon } from 'lucide-react';
import { markLoggedOut } from '@/auth/login-gate';
import { resolveTheme, toggleTheme } from '@/lib/theme';

// Maps the leading path segment to the page title shown on the left of the topbar.
const TITLES: Record<string, string> = {
  dashboard: 'Dashboard',
  deployments: 'Deployments',
  models: 'Models',
  'swap-profiles': 'Swap Profiles',
  conversations: 'Conversations',
  'audit-log': 'Audit Log',
  settings: 'Settings',
};

function pageTitle(pathname: string): string {
  const seg = pathname.split('/').filter(Boolean)[0] ?? 'dashboard';
  return TITLES[seg] ?? 'EchoProxy';
}

export function Topbar(): JSX.Element {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = accounts[0];
  const location = useLocation();
  const [isDark, setIsDark] = useState(() => resolveTheme() === 'dark');

  return (
    <header className="flex h-14 items-center justify-between border-b border-border bg-topbar px-6">
      <h1 className="text-base font-medium text-heading">{pageTitle(location.pathname)}</h1>
      <div className="flex items-center gap-3">
        <button
          type="button"
          aria-label="Téma váltása"
          title="Téma váltása"
          className="flex h-8 w-8 items-center justify-center rounded-full text-heading transition-colors hover:bg-black/5 dark:hover:bg-white/10"
          onClick={() => setIsDark(toggleTheme() === 'dark')}
        >
          {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
        {isAuthenticated && account ? (
          <>
            <div className="flex items-center gap-2 text-sm text-heading">
              <UserIcon className="h-4 w-4" />
              <span>{account.name ?? account.username}</span>
            </div>
            <button
              className="btn-secondary"
              onClick={() => {
                // Tells the next load that this is a logout, not a first visit — without
                // it the login gate would redirect straight back to the IdP (EW-190).
                markLoggedOut();
                void instance.logoutRedirect({
                  account,
                  // Keycloak's RP-initiated logout rejects the call without an
                  // id_token_hint, and MSAL only sends one when the request carries it.
                  // Entra does not require it, so passing it is safe on both paths.
                  idTokenHint: account.idToken,
                  postLogoutRedirectUri: window.location.origin,
                });
              }}
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </>
        ) : null}
      </div>
    </header>
  );
}
